import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Avatar,
  IconButton,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { PhotoCamera as PhotoCameraIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const validationSchema = yup.object({
  name: yup
    .string()
    .required('Name is required')
    .max(100, 'Name is too long'),
  email: yup
    .string()
    .email('Enter a valid email')
    .required('Email is required'),
  current_password: yup
    .string()
    .when('new_password', {
      is: (val) => val && val.length > 0,
      then: yup.string().required('Current password is required'),
    }),
  new_password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number and one special character'
    ),
  confirm_password: yup
    .string()
    .oneOf([yup.ref('new_password'), null], 'Passwords must match'),
});

function Profile() {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [preferences, setPreferences] = useState({
    sms_parsing: true,
    receipt_scanning: true,
    email_notifications: true,
    monthly_reports: true,
  });

  const formik = useFormik({
    initialValues: {
      name: user?.name || '',
      email: user?.email || '',
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError('');
        setSuccess('');

        const updateData = {
          name: values.name,
          email: values.email,
        };

        if (values.new_password) {
          updateData.current_password = values.current_password;
          updateData.new_password = values.new_password;
        }

        const response = await axios.put(
          'https://stu148664.webhosting.arden.ac.uk/COM6008/api/users/profile',
          updateData
        );

        updateUser(response.data);
        setSuccess('Profile updated successfully');
        formik.resetForm({
          values: {
            ...values,
            current_password: '',
            new_password: '',
            confirm_password: '',
          },
        });
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to update profile');
      } finally {
        setLoading(false);
      }
    },
  });

  const handlePreferenceChange = async (preference) => {
    try {
      setLoading(true);
      setError('');
      const newPreferences = {
        ...preferences,
        [preference]: !preferences[preference],
      };
      
      await axios.put('https://stu148664.webhosting.arden.ac.uk/COM6008/api/users/preferences', {
        preferences: newPreferences,
      });
      
      setPreferences(newPreferences);
      setSuccess('Preferences updated successfully');
    } catch (err) {
      setError('Failed to update preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        setLoading(true);
        setError('');
        const formData = new FormData();
        formData.append('avatar', file);

        const response = await axios.put(
          'https://stu148664.webhosting.arden.ac.uk/COM6008/api/users/avatar',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        updateUser(response.data);
        setSuccess('Profile picture updated successfully');
      } catch (err) {
        setError('Failed to update profile picture');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Profile Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                src={user?.avatar_url}
                sx={{ width: 100, height: 100, mr: 2 }}
              />
              <Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="avatar-upload"
                  type="file"
                  onChange={handleAvatarChange}
                />
                <label htmlFor="avatar-upload">
                  <IconButton
                    color="primary"
                    component="span"
                    disabled={loading}
                  >
                    <PhotoCameraIcon />
                  </IconButton>
                </label>
                <Typography variant="body2" color="textSecondary">
                  Click to change profile picture
                </Typography>
              </Box>
            </Box>

            <form onSubmit={formik.handleSubmit}>
              <TextField
                fullWidth
                margin="normal"
                name="name"
                label="Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
              <TextField
                fullWidth
                margin="normal"
                name="email"
                label="Email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ mt: 2 }}
                disabled={loading}
              >
                Update Profile
              </Button>
            </form>
          </Paper>
        </Grid>

        {/* Password Change */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Change Password
            </Typography>
            <form onSubmit={formik.handleSubmit}>
              <TextField
                fullWidth
                margin="normal"
                name="current_password"
                label="Current Password"
                type="password"
                value={formik.values.current_password}
                onChange={formik.handleChange}
                error={
                  formik.touched.current_password &&
                  Boolean(formik.errors.current_password)
                }
                helperText={
                  formik.touched.current_password && formik.errors.current_password
                }
              />
              <TextField
                fullWidth
                margin="normal"
                name="new_password"
                label="New Password"
                type="password"
                value={formik.values.new_password}
                onChange={formik.handleChange}
                error={
                  formik.touched.new_password &&
                  Boolean(formik.errors.new_password)
                }
                helperText={
                  formik.touched.new_password && formik.errors.new_password
                }
              />
              <TextField
                fullWidth
                margin="normal"
                name="confirm_password"
                label="Confirm New Password"
                type="password"
                value={formik.values.confirm_password}
                onChange={formik.handleChange}
                error={
                  formik.touched.confirm_password &&
                  Boolean(formik.errors.confirm_password)
                }
                helperText={
                  formik.touched.confirm_password &&
                  formik.errors.confirm_password
                }
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ mt: 2 }}
                disabled={loading}
              >
                Change Password
              </Button>
            </form>
          </Paper>
        </Grid>

        {/* Preferences */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Preferences
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.sms_parsing}
                      onChange={() => handlePreferenceChange('sms_parsing')}
                      disabled={loading}
                    />
                  }
                  label="SMS Parsing"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.receipt_scanning}
                      onChange={() => handlePreferenceChange('receipt_scanning')}
                      disabled={loading}
                    />
                  }
                  label="Receipt Scanning"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.email_notifications}
                      onChange={() =>
                        handlePreferenceChange('email_notifications')
                      }
                      disabled={loading}
                    />
                  }
                  label="Email Notifications"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.monthly_reports}
                      onChange={() => handlePreferenceChange('monthly_reports')}
                      disabled={loading}
                    />
                  }
                  label="Monthly Reports"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Profile; 